import { useCallback, useEffect, useRef } from 'react'
import { trailPathsAt, type ReplayProjection, type ReplayTrailPath } from '../../lib/replayTrails'
import type { ReplayBundle } from '../../lib/replayTypes'

export interface ReplayTrailCanvasProps {
  bundle: ReplayBundle
  hiddenParticipants: ReadonlySet<string>
  /** Live cursor, read inside the draw frame so a redraw never waits on React. */
  cursorRef: React.MutableRefObject<number>
  projectRef: React.MutableRefObject<ReplayProjection>
  size: { w: number; h: number }
  isPlaying: boolean
  /**
   * Bumped once per MapLibre `move` event — a pan, a zoom, or a programmatic
   * `fitBounds`/`jumpTo` — by the viewer, which owns the live map. The canvas does
   * not read the camera directly, only through `projectRef`, but it has to know
   * when the camera moved so a paused view still redraws after a pan, a zoom, or a
   * scrub. While playing, the rAF loop below already redraws every frame
   * regardless of camera motion, so only the paused branch reads this.
   */
  viewEpoch: number
  cursor: number
}

/** Dogs draw heavier than hunters: the dog's line is the one being read. */
const DOG_TRAIL_WIDTH_PX = 3
const HUNTER_TRAIL_WIDTH_PX = 2
const HUNTER_DASH_PATTERN = [6, 5]
/** Hoisted so clearing the dash costs no allocation on the per-frame path. */
const NO_DASH: number[] = []

/**
 * The barking-span red, verbatim mobile's `replayBarkingColor`
 * (`replay_trail_colors.dart`), so the same spans read the same on both clients.
 *
 * Deliberately NOT the badge red (`REPLAY_BARK_RED` / `BARKING_RED`, #EB3B5A):
 * mobile uses a separate colour for the barking label and icon border
 * (`labelBarkingRed`, #EE2F0E) than for the trail, so the badge constant is the
 * wrong one to reuse here.
 */
const BARKING_TRAIL_RED = '#FF0000'

/**
 * Trails are drawn slightly translucent so crossing lines stay separable and the
 * terrain beneath still reads — several dogs working the same ground overlap
 * constantly, and opaque lines turn that into one indistinct mass.
 */
const TRAIL_ALPHA = 0.75

/**
 * Draws the progressive trails.
 *
 * This is the one layer that redraws every frame, and the reason it still holds
 * on the MapLibre engine: a four-hour hunt with ten carriers is tens of thousands
 * of points, and driving the same trail as a MapLibre GeoJSON source would mean
 * calling `setData` with that many coordinates on every frame — which
 * re-tessellates the whole line from scratch on the GPU side each time rather
 * than appending to what is already drawn, the same cost the DOM-node approach
 * had, just moved one layer down the stack. Everything interactive — markers,
 * chips, pins — stays on MapLibre's own DOM-marker layer above this canvas.
 */
export function ReplayTrailCanvas({
  bundle,
  hiddenParticipants,
  cursorRef,
  projectRef,
  size,
  isPlaying,
  viewEpoch,
  cursor,
}: ReplayTrailCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Read through refs inside the draw call: while playing it runs from an
  // animation frame that must not be torn down and rebuilt on every render.
  const bundleRef = useRef(bundle)
  bundleRef.current = bundle
  const hiddenRef = useRef(hiddenParticipants)
  hiddenRef.current = hiddenParticipants

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || size.w === 0 || size.h === 0) return

    const dpr = window.devicePixelRatio ?? 1
    // Resizing the backing store clears it, so only touch it when the physical
    // size actually changed.
    const targetW = Math.round(size.w * dpr)
    const targetH = Math.round(size.h * dpr)
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW
      canvas.height = targetH
    }

    const context = canvas.getContext('2d')
    if (!context) return

    context.setTransform(dpr, 0, 0, dpr, 0, 0)
    context.clearRect(0, 0, size.w, size.h)
    context.lineJoin = 'round'
    context.lineCap = 'round'
    context.globalAlpha = TRAIL_ALPHA

    const paths = trailPathsAt(
      bundleRef.current,
      cursorRef.current,
      hiddenRef.current,
      projectRef.current,
    )

    // Two passes: every base run first, then every barking run. Within one pass
    // the participants are drawn in bundle order, so a single pass would let a
    // later dog's base line paint over an earlier dog's barking span — several
    // dogs working the same ground overlap constantly.
    //
    // One stroke per participant per pass, never one per run: colour, width and
    // dash are constant within a pass, and disjoint subpaths inside a single path
    // stroke identically to separate strokes. Trails are cumulative, so a
    // per-run stroke would re-issue a full state-and-stroke cycle for every
    // barking transition since hunt start, on every frame.
    const strokePass = (path: ReplayTrailPath, barking: boolean) => {
      let opened = false
      for (let s = 0; s < path.segments.length; s++) {
        const segment = path.segments[s]
        if (segment.barking !== barking) continue
        const points = segment.points
        // The split never yields a one-point run; this guards the public boundary.
        if (points.length < 2) continue
        if (!opened) {
          context.strokeStyle = barking ? BARKING_TRAIL_RED : path.color
          context.lineWidth = path.dashed ? HUNTER_TRAIL_WIDTH_PX : DOG_TRAIL_WIDTH_PX
          context.setLineDash(path.dashed ? HUNTER_DASH_PATTERN : NO_DASH)
          context.beginPath()
          opened = true
        }
        context.moveTo(points[0].x, points[0].y)
        for (let i = 1; i < points.length; i++) {
          context.lineTo(points[i].x, points[i].y)
        }
      }
      // A participant with no runs of this kind gets no empty stroke at all.
      if (opened) context.stroke()
    }

    for (let p = 0; p < paths.length; p++) strokePass(paths[p], false)
    for (let p = 0; p < paths.length; p++) strokePass(paths[p], true)
  }, [size, cursorRef, projectRef])

  // Playing: the trail grows continuously, so it redraws from an animation frame.
  useEffect(() => {
    if (!isPlaying) return
    let frame = 0
    const loop = () => {
      draw()
      frame = requestAnimationFrame(loop)
    }
    frame = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(frame)
  }, [isPlaying, draw])

  // Paused: nothing animates, so one draw per change is the whole job. Listing
  // viewEpoch and cursor here is what makes a pan, zoom or scrub repaint — the
  // draw itself reads refs, so without these it would never be told.
  useEffect(() => {
    if (isPlaying) return
    draw()
  }, [isPlaying, draw, viewEpoch, cursor, hiddenParticipants, bundle])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  )
}
